import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toPascalCase } from "@/utils/helpers";
import {
  BookText,
  CalendarDays,
  ChartPie,
  DollarSign,
  Eye,
  Info,
  Layers,
  Mail,
  Phone,
  Plus,
  Text,
  Trash2,
  User,
  Users,
} from "lucide-react";
import { DateTimeline } from "../contract/ContractDetails";
import { useNavigate, useOutletContext } from "react-router-dom";
import TextEditor from "@/ui/TextEditor";
import {
  populateDeepInsights,
  updatePursuit,
} from "@/features/pursuit/api/apiPursuit";
import PursuitStatusSelect from "./PursuitStatusSelect";
import { useOptimisticUpdate } from "@/hooks/useOptimisticUpdate";
import DatePicker from "@/components/ui/date-picker";
import AutoSaveInput from "@/ui/AutoSaveInput";
import PursuitOperations from "./PursuitOperations";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { useAddPursuitContact } from "./useAddPursuitContact";
import { useRemovePursuitContact } from "./useRemovePursuitContact";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useUpdatePursuitContact } from "./useUpdatePursuitContact";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCreateBlockNote } from "@blocknote/react";
import { Skeleton } from "@/components/ui/skeleton";
import { PiMagicWandDuotone } from "react-icons/pi";
import toast from "react-hot-toast";

const formSchema = z.object({
  name: z.string().trim().min(1, {
    message: "Title cannot be empty",
  }),
  phone: z.string().trim().optional(),
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .optional()
    .or(z.literal("")), // Allow empty string as a valid value,
  role: z.string().trim().optional(),
});

function OverviewLabel({ label, icon: Icon }) {
  return (
    <span className="flex items-center gap-2 text-muted-foreground">
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </span>
  );
}

function OverviewRow({ label, value, icon: Icon }) {
  if (!value) {
    return null;
  }
  return (
    <>
      <OverviewLabel label={label} icon={Icon} />
      <span>{value}</span>
    </>
  );
}

function AddOrEditContactButton({
  pursuitId,
  contact, // optional: if present, it's edit mode
  children,
  ...props
}) {
  const [open, setOpen] = useState(false);

  const isEditMode = !!contact;
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: contact?.name || undefined,
      role: contact?.role || undefined,
      phone: contact?.phone || undefined,
      email: contact?.email || undefined,
    },
  });

  const { mutate: addContact, isLoading: isAddingContact } =
    useAddPursuitContact(pursuitId);
  const { mutate: updateContact, isLoading: isUpdatingContact } =
    useUpdatePursuitContact(pursuitId); // you'll need to define this

  const onSubmit = (values) => {
    if (isEditMode) {
      updateContact(
        { ...values, contact_id: contact.id } // include the contact ID
      );
    } else {
      addContact({ ...values, pursuit_id: pursuitId });
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isEditMode ? (
        <DialogTrigger className="hover:underline text-start" {...props}>
          {children}
        </DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={isAddingContact}
            {...props}
          >
            <Plus className="h-4 w-4" />
            Add Contact
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Contact" : "Add New Contact"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-4"
          >
            {/* name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* role */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <FormControl>
                    <Input placeholder="E.g. Project Manager" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter phone number"
                      type="tel"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter email address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isAddingContact || isUpdatingContact}
              >
                {isEditMode ? "Update Contact" : "Save Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function Contacts({ pursuitId, contacts }) {
  const { mutate: deleteContact, isLoading: isDeletingContact } =
    useRemovePursuitContact(pursuitId);

  return (
    <div className="w-full">
      {contacts && contacts.length > 0 && (
        <div className="flex justify-between items-center mb-8">
          <AddOrEditContactButton pursuitId={pursuitId} />
        </div>
      )}

      {!contacts || contacts?.length === 0 ? (
        <div className="text-center py-4 border border-dashed rounded-lg text-muted-foreground ">
          <User className="mx-auto h-8 w-8 stroke-1" />
          <h2 className="mt-4 text-xl font-medium">No contacts yet</h2>
          <p className="text-muted-foreground mt-2">
            Add your first contact to get started
          </p>
          <div className="mt-4">
            <AddOrEditContactButton pursuitId={pursuitId} variant={"muted"} />
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {contacts
            ?.sort((a, b) => a.name.localeCompare(b.name))
            .map((contact) => (
              <Card key={contact.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <CardTitle className="flex justify-between items-center">
                    <AddOrEditContactButton
                      pursuitId={pursuitId}
                      contact={contact}
                      variant={"muted"}
                    >
                      {contact.name}
                    </AddOrEditContactButton>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          disabled={isDeletingContact}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently
                            delete your contact.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            variant={"destructive"}
                            onClick={() => deleteContact(contact.id)}
                          >
                            <Trash2 className="h-4 w-4" /> Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardTitle>
                  {contact?.role && (
                    <CardDescription className="">
                      {contact.role}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  {contact.phone && (
                    <div className="flex items-center mt-2">
                      <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{contact.phone}</span>
                    </div>
                  )}
                  {contact.email && (
                    <div className="flex items-center mt-2">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                      <a
                        href={`mailto:${contact.email}`}
                        className=" hover:underline hover:cursor-pointer"
                      >
                        {contact.email}
                      </a>
                    </div>
                  )}
                  {!contact.phone && !contact.email && (
                    <div className="text-muted-foreground text-sm italic">
                      No contact details provided
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}

function PursuitOverview() {
  const { pursuit } = useOutletContext();
  const pursuit_id = pursuit?.id;
  const { mutate: update } = useOptimisticUpdate(updatePursuit, [
    "pursuit",
    pursuit_id,
  ]);
  const editor = useCreateBlockNote();

  const queryClient = useQueryClient();
  const { mutate: populate, isLoading: isPopulating } = useMutation({
    mutationFn: populateDeepInsights,
    onSuccess: async (data) => {
      const blocks = await editor.tryParseMarkdownToBlocks(data);
      await updatePursuit({ pursuit_id, award_reason: { blocks } });
      queryClient.invalidateQueries(["pursuit", pursuit_id]);
    },
    onError: (e) => {
      if (e.response?.data?.detail) toast.error(e.response?.data?.detail);
      else toast.error(e.message);
    },
  });

  const navigate = useNavigate();

  const headerData = [
    {
      label: "Status",
      icon: ChartPie,
      value: (
        <PursuitStatusSelect
          defaultValue={pursuit?.status}
          onValueChange={(status) => update({ pursuit_id, status })}
        />
      ),
    },
    {
      label: "Due Date",
      icon: CalendarDays,
      value: (
        <DatePicker
          selected={pursuit?.due_date && new Date(pursuit.due_date)}
          onSelect={(due_date) => update({ pursuit_id, due_date })}
          placeholder="Pick due date"
          className="border-none bg-transparent w-full"
        />
      ),
    },
    {
      label: "Type",
      icon: BookText,
      value: (
        <span className="ml-4">
          {" "}
          {["sled", "sbir"].includes(pursuit.search_type)
            ? pursuit.search_type.toUpperCase()
            : toPascalCase(pursuit.search_type)}
        </span>
      ),
    },
    {
      label: "Value",
      icon: DollarSign,
      value: pursuit?.value,
    },
  ];

  const timelineData = [
    {
      title: "Created",
      date: pursuit?.created_at,
    },
    {
      title: "Due Date",
      date: pursuit?.due_date,
    },
    {
      title: "Last Updated",
      date: pursuit?.updated_at,
    },
  ];
  return (
    <div className="grid grid-cols-[1fr_15rem] gap-4 pb-12">
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center group gap-2">
            <AutoSaveInput
              initialValue={pursuit?.title}
              onSave={async (title) =>
                await updatePursuit({ pursuit_id, title })
              }
              placeholder="Task title"
              className="!text-xl focus:ring-0 w-full hover:bg-muted"
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/contract/${pursuit.search_type}/${pursuit.proposal_id}`
                );
              }}
              disabled={pursuit?.search_type === "upload"}
            >
              <Eye className="" />
              View Opportunity
            </Button>
            <PursuitOperations pursuit={pursuit} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[10rem_1fr] items-center gap-y-4">
            {headerData.map((item, i) => (
              <OverviewRow
                key={i}
                label={item.label}
                value={item.value}
                icon={item.icon}
              />
            ))}
          </div>
          <Separator className="my-4 bg-muted/50" />
          <div className="flex flex-col items-start gap-y-2 mt-4">
            <div className="flex justify-between items-center w-full">
              <OverviewLabel
                label={
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-2">
                        Deep Insights{" "}
                        <span
                          className="text-muted-foreground/40"
                          title="Optional"
                        >
                          <Info className="h-4 w-4" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm" side="right">
                        Deep insights are used to tailor the proposal to your
                        business. Clearly explain why your company should be
                        awarded this opportunity by providing key insights
                        specific to this opportunity.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                }
                icon={Layers}
              />
              <Button variant="secondary" onClick={() => populate(pursuit_id)}>
                <PiMagicWandDuotone />
                Generate for Me{" "}
              </Button>
            </div>
            {isPopulating ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <TextEditor
                initialBlocks={pursuit?.award_reason?.blocks}
                saveDataFn={async (content) => {
                  await updatePursuit({ pursuit_id, award_reason: content });
                }}
              />
            )}
            <Separator className="my-4 bg-muted/50" />
          </div>
          <div className="flex flex-col items-start gap-y-2 mt-4">
            <OverviewLabel label={"Description"} icon={Text} />
            <TextEditor
              initialBlocks={pursuit?.description?.blocks}
              saveDataFn={async (content) =>
                await updatePursuit({ pursuit_id, description: content })
              }
            />
            <Separator className="my-4 bg-muted/50" />
          </div>
          <div className="flex flex-col items-start gap-y-2 mt-4">
            <OverviewLabel label={"Contacts"} icon={Users} />
            <Contacts contacts={pursuit?.contacts} pursuitId={pursuit_id} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <DateTimeline
          className="justify-self-center py-6"
          dates={timelineData}
        />
      </Card>
    </div>
  );
}

export default PursuitOverview;
